import {
  deleteGuest,
  getGuestListWithSettings,
  saveGuest,
} from 'portal-modules/guests/api/api'
import { all, call, put, takeLatest } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import { addCustomContent } from 'portal-modules/common/redux/modules/customContent'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import createFormAction from 'portal-modules/common/redux/create-form-action'

// action types
export const REQUEST_GUEST_LIST = 'rp/guests/REQUEST_GUEST_LIST'
export const RECEIVE_GUEST_LIST = 'rp/guests/RECEIVE_GUEST_LIST'
export const REQUEST_ADD = 'rp/guests/REQUEST_ADD'
export const RECEIVE_ADD = 'rp/guests/RECEIVE_ADD'
export const REQUEST_UPDATE = 'rp/guests/REQUEST_UPDATE'
export const RECEIVE_UPDATE = 'rp/guests/RECEIVE_UPDATE'
export const REQUEST_DELETE = 'rp/guests/REQUEST_DELETE'
export const RECEIVE_DELETE = 'rp/guests/RECEIVE_DELETE'

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_GUEST_LIST:
    case RECEIVE_ADD:
    case RECEIVE_UPDATE:
      return { ...state, ...payload }
    case RECEIVE_DELETE:
      return Object.values(state).reduce((obj, guest) => {
        if (guest.id === payload) return obj
        return { ...obj, [guest.id]: guest }
      }, {})
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const requestGuestList = () => ({
  type: REQUEST_GUEST_LIST,
})

export const receiveGuestList = payload => ({
  type: RECEIVE_GUEST_LIST,
  payload,
})

export const requestAddGuest = createFormAction(REQUEST_ADD)

export const receiveAddGuest = payload => ({
  type: RECEIVE_ADD,
  payload,
})

export const requestUpdateGuest = createFormAction(REQUEST_UPDATE)

export const receiveUpdateGuest = payload => ({
  type: RECEIVE_UPDATE,
  payload,
})

export const requestDeleteGuest = payload => ({
  type: REQUEST_DELETE,
  payload,
})

export const receiveDeleteGuest = payload => ({
  type: RECEIVE_DELETE,
  payload,
})

// sagas
export function* fetchGuestList() {
  const { guests, settings } = yield call(getGuestListWithSettings)
  const guestsObject = guests
    ? guests.reduce((obj, guest) => ({ ...obj, [guest.id]: guest }), {})
    : {}
  let actions = []
  actions.push(receiveGuestList(guestsObject))
  if (Object.keys(settings).length) {
    actions.push(addCustomContent({ guests: settings.custom_text }))
    yield put(addSettings({ guests: settings }))
  }
  yield all(actions.map(a => put(a)))
}

export function* watchRequestGuestList() {
  yield takeLatest(
    REQUEST_GUEST_LIST,
    createRequestSaga(fetchGuestList, {
      key: 'guests',
      message: __('Looking for your guests'),
    }),
  )
}

export function* addOrUpdateGuest({ payload, type }) {
  const data = yield call(saveGuest, payload)
  const guestObject = { [data.guest.id]: data.guest }
  yield put(
    type === REQUEST_ADD
      ? receiveAddGuest(guestObject)
      : receiveUpdateGuest(guestObject),
  )
}

export function* watchRequestAdd() {
  yield takeLatest(
    REQUEST_ADD,
    createRequestSaga(addOrUpdateGuest, {
      key: 'guests',
      message: __('Adding guest'),
      successMessage: __('Guest added'),
      write: true,
    }),
  )
}

export function* watchRequestUpdate() {
  yield takeLatest(
    REQUEST_UPDATE,
    createRequestSaga(addOrUpdateGuest, {
      key: 'guests',
      message: __('Updating guest details'),
      successMessage: __('Details updated'),
      write: true,
    }),
  )
}

export function* postDeleteGuest({ payload }) {
  yield call(deleteGuest, { id: payload })
  yield put(receiveDeleteGuest(payload))
}

export function* watchRequestDelete() {
  yield takeLatest(
    REQUEST_DELETE,
    createRequestSaga(postDeleteGuest, {
      key: 'guests',
      message: __('Deleting guest'),
      successMessage: __('Guest deleted'),
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestGuestList,
  watchRequestAdd,
  watchRequestDelete,
  watchRequestUpdate,
]
